import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    validateFirstname,
    ContactTypes,
    validateLastname,
    validateContactEmail,
    validateMobileNumber,
    validateSalesReportingAfterCopy,
} from '../../../Validation';
import some from 'lodash/some';
import { SectionProps } from '../../types';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import Panel from '@sharedComponents/Panel';
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import InputText from "@sharedComponents/InputText";

const SalesReportingForm = (props: SectionProps) => {
    const {
        stepValues,
        setStepValues,
        onChangeValues,
        formErrors,
        setFormErrors,
    } = props
    const { t } = useTranslation()

    const onChangeFirstname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            salesReporting: {
                ...stepValues.contactInfo?.salesReporting,
                firstname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateFirstname(
            value,
            formErrors,
            ContactTypes.SalesReporting
        )
        setFormErrors(errors)
    }

    const onChangeLastname = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            salesReporting: {
                ...stepValues.contactInfo?.salesReporting,
                lastname: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        onChangeValues && onChangeValues({ contactInfo: obj })

        const errors = validateLastname(
            value,
            formErrors,
            ContactTypes.SalesReporting
        )
        setFormErrors(errors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            salesReporting: {
                ...stepValues.contactInfo?.salesReporting,
                email: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateContactEmail(
            value,
            formErrors,
            ContactTypes.SalesReporting
        )
        setFormErrors(errors)
    }

    const onChangeMobile = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            salesReporting: {
                ...stepValues.contactInfo?.salesReporting,
                mobileNumber: value,
            },
        }

        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        const errors = validateMobileNumber(
            value,
            formErrors,
            ContactTypes.SalesReporting
        )
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const obj = {
            ...stepValues.contactInfo,
            salesReporting: {
                ...stepValues.contactInfo?.salesReporting,
                phoneNumber: value,
            },
        }
        setStepValues({ ...stepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })
    }

    const copyFromProductManager = () => {
        const fields = {
            firstname: stepValues.contactInfo?.productManager?.firstname,
            lastname: stepValues.contactInfo?.productManager?.lastname,
            email: stepValues.contactInfo?.productManager?.email,
            mobileNumber: stepValues.contactInfo?.productManager?.mobileNumber,
            phoneNumber: stepValues.contactInfo?.productManager?.phoneNumber,
        }

        const obj = {
            ...stepValues.contactInfo,
            salesReporting: fields,
        }
        setStepValues({ ...setStepValues, contactInfo: obj })
        props.onChangeValues && props.onChangeValues({ contactInfo: obj })

        validateSalesReportingAfterCopy(fields, formErrors).then(
            (errors: FormErrorType[]) => {
                setFormErrors(errors)
            }
        )
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='producer-form producer-panel-wrapper'>
            <Panel className="panel--crb">
                <h3>{t('companyRegistration.salesReporting')}</h3>
                <Button
                    type={ButtonType.PRIMARY_ALT}
                    className="copy"
                    text={t('companyRegistration.copyFromProductManager')}
                    onClick={copyFromProductManager}
                />
                <div className='producer-form__fields'>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sr-firstname"
                            label={t('global.firstname')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.SalesReporting}Firstname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.salesReporting
                                    ?.firstname || ''
                            }
                            maxLength={50}
                            onChange={onChangeFirstname}
                            autoComplete="given-name"
                            errorText={uniqueErrors(`${ContactTypes.SalesReporting}Firstname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sr-lastname"
                            label={t('global.lastname')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.SalesReporting}Lastname`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.salesReporting
                                    ?.lastname || ''
                            }
                            maxLength={50}
                            onChange={onChangeLastname}
                            autoComplete="family-name"
                            errorText={uniqueErrors(`${ContactTypes.SalesReporting}Lastname`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sr-email"
                            label={t('global.email')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.SalesReporting}Email`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.salesReporting?.email ||
                                ''
                            }
                            onChange={onChangeEmail}
                            type="email"
                            autoComplete="email"
                            maxLength={250}
                            errorText={uniqueErrors(`${ContactTypes.SalesReporting}Email`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sr-mobile"
                            label={t('global.mobileNumber')}
                            required={true}
                            hasError={
                                some(formErrors, [
                                    'key',
                                    `${ContactTypes.SalesReporting}MobileNumber`,
                                ])
                            }
                            value={
                                stepValues.contactInfo?.salesReporting
                                    ?.mobileNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangeMobile}
                            autoComplete="tel"
                            errorText={uniqueErrors(`${ContactTypes.SalesReporting}MobileNumber`)}
                        />
                    </div>
                    <div className='producer-form__field'>
                        <InputText
                            id="crb-sr-phone"
                            label={t('global.phoneNumber')}
                            value={
                                stepValues.contactInfo?.salesReporting
                                    ?.phoneNumber || ''
                            }
                            maxLength={30}
                            onChange={onChangePhone}
                            autoComplete="tel"
                        />
                    </div>
                </div>
            </Panel>
        </div>
    )
}

export default SalesReportingForm;